import React from 'react';

import { MODAL_REGISTRY } from 'context/modal/registry';
import { ModalKey, ModalKeyContext } from './modal_key_context';
import { CloseFunction, ModalContext, OpenFunction } from './modal_context';

type ModalState = Record<
  string,
  { open: boolean; props: React.ComponentProps<any> }
>;

export function ModalProvider({ children }: React.PropsWithChildren) {
  const [modals, setModals] = React.useState<ModalState>({});

  const open: OpenFunction = React.useCallback((key, ...[props = {}]) => {
    setModals((previousState) =>
      sanitize(key, {
        ...previousState,
        [key]: { props, open: true },
      })
    );
  }, []);

  const close: CloseFunction = React.useCallback((key) => {
    if (!key) {
      return;
    }

    setModals((previousState) => {
      const newState = { ...previousState };

      if (key in newState) {
        newState[key] = { ...newState[key], open: false };
      }

      return sanitize(key, newState);
    });
  }, []);

  return (
    <ModalContext.Provider value={{ open, close }}>
      {children}
      {Object.entries(modals).map(([key, { open, props }]) => {
        const modalKey = key as ModalKey;
        const Modal = MODAL_REGISTRY[modalKey] as any;
        return (
          <ModalKeyContext.Provider key={key} value={modalKey}>
            <Modal {...props} key={key} open={open} />
          </ModalKeyContext.Provider>
        );
      })}
    </ModalContext.Provider>
  );
}

function sanitize(key: ModalKey, state: ModalState) {
  const modals = { ...state };

  for (const modalKey in modals) {
    const modal = modals[modalKey];
    if (modalKey !== key && modal && !modal.open) {
      delete modals[modalKey];
    }
  }

  return modals;
}
